import React, { useState } from 'react';
import CopyButton from './CopyButton';
import './StepByStepGuide.css';

const StepByStepGuide = ({ title, description, steps, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState([]);

  const handleStepComplete = (stepIndex) => {
    if (!completedSteps.includes(stepIndex)) {
      setCompletedSteps([...completedSteps, stepIndex]);
    }

    if (stepIndex === steps.length - 1 && onComplete) {
      onComplete();
    }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      handleStepComplete(currentStep);
      setCurrentStep(currentStep + 1);
    } else {
      handleStepComplete(currentStep);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const progress = Math.round((completedSteps.length / steps.length) * 100);
  const step = steps[currentStep];

  return (
    <div className="step-guide-container">
      <div className="step-guide-header">
        <div className="step-guide-title">
          <span className="guide-icon">🎯</span>
          <div>
            <h2>{title}</h2>
            <p>{description}</p>
          </div>
        </div>
        <div className="step-guide-progress">
          <div className="progress-text">{progress}% complété</div>
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }}></div>
          </div>
        </div>
      </div>

      <div className="step-navigation">
        {steps.map((s, index) => (
          <button
            key={index}
            className={`step-dot ${index === currentStep ? 'active' : ''} ${
              completedSteps.includes(index) ? 'completed' : ''
            }`}
            onClick={() => setCurrentStep(index)}
          >
            {completedSteps.includes(index) ? (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              index + 1
            )}
          </button>
        ))}
      </div>

      <div className="step-content">
        <div className="step-header">
          <span className="step-number">Étape {currentStep + 1}/{steps.length}</span>
          <h3>{step.title}</h3>
        </div>

        <div className="step-body">
          {step.description && (
            <p className="step-description">{step.description}</p>
          )}

          {step.image && (
            <div className="step-image">
              <img src={step.image} alt={step.title} />
            </div>
          )}

          {step.code && (
            <div className="step-code">
              <div className="code-header">
                <span className="code-language">{step.codeLanguage || 'code'}</span>
                <CopyButton text={step.code} label="Copier" />
              </div>
              <pre><code>{step.code}</code></pre>
            </div>
          )}

          {step.prompt && (
            <div className="step-prompt">
              <div className="prompt-header">
                <span>💡 Prompt à utiliser</span>
                <CopyButton text={step.prompt} label="Copier le prompt" />
              </div>
              <pre>{step.prompt}</pre>
            </div>
          )}

          {step.tips && (
            <div className="step-tips">
              <h4>💡 Conseils</h4>
              <ul>
                {step.tips.map((tip, i) => (
                  <li key={i}>{tip}</li>
                ))}
              </ul>
            </div>
          )}

          {step.warning && (
            <div className="step-warning">
              <span className="warning-icon">⚠️</span>
              <p>{step.warning}</p>
            </div>
          )}

          {step.checkpoint && (
            <div className="step-checkpoint">
              <h4>✅ Checkpoint</h4>
              <p>{step.checkpoint}</p>
            </div>
          )}
        </div>

        <div className="step-actions">
          <button
            className="btn btn-secondary"
            onClick={handlePrev}
            disabled={currentStep === 0}
          >
            ← Précédent
          </button>

          <button
            className="btn btn-primary"
            onClick={handleNext}
          >
            {currentStep === steps.length - 1 ? (
              completedSteps.includes(currentStep) ? '✓ Terminé !' : 'Terminer'
            ) : (
              'Suivant →'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default StepByStepGuide;
